import { EmcDisplayDataManager } from "./EmcDisplayDataManager";
import EmcConsts from "./EmcConsts";
import SystemTesterConsts from "./SystemTesterConsts";
import SystemTesterFaultWarningMessages from "./SystemTesterFaultWarningMessages";
export class SystemTesterDisplayFacade {
    constructor() {
        this.moduleNames = [
            'Arm',
            'Drive',
            'Junction Box',
            'PSGUI',
            'CSGUI',
            'Rack',
            'PIM'
        ];
        this.faultWarningMessages = new SystemTesterFaultWarningMessages();
        this.allSystemsList = [];
        this.emcDisplayDataManager = new EmcDisplayDataManager();
        this.emcSignals = this.emcDisplayDataManager.emcSignals;
        this.activeSystemsList = [];
        this.prevActiveSystemsList = [];
        this.patientSystemCount = 0;
        this.controlSystemCount = 0;
        this.areSystemTestsRunning = false;
        this.disabledStates = [true, true, true, true, true, true, true, true, true];
        this.disabledPasses = [true, true, true, true, true, true, true, true, true];
        this.disabledFailures = [true, true, true, true, true, true, true, true, true];
        this.currentRunningTest = 0;
        this.parentTestResults = [];
        this.hasTestEnded = false;
        this.previousPackets = new Map();
        this.messageText = '';
        this.logEntries = [];
        this.serialNumber = '';
        this.subsystemSerialNumbers = this.createEmptySubsystemSerialNumbers();
        this.isSerialNumberLocked = false;
        this.isSubsystemSerialNumbersLocked = false;
        this.testerFirstName = '';
        this.testerLastName = '';
        this.isTesterNameLocked = false;
        this.startSystemTestMonitorTimeStamp = 0;
        this.systemTestRuntime = '0';
    }
    static getSystemTesterDisplayFacade() {
        if (null == SystemTesterDisplayFacade.systemTesterDisplayFacade) {
            SystemTesterDisplayFacade.systemTesterDisplayFacade = new SystemTesterDisplayFacade();
        }
        return SystemTesterDisplayFacade.systemTesterDisplayFacade;
    }
    populateAllSystemList() {
        return this.emcDisplayDataManager.fetchAllSystemData().
            then(() => { this.allSystemsList = this.emcDisplayDataManager.allSystemsList; });
    }
    createEmptySubsystemSerialNumbers() {
        const serialNumbers = {};
        this.moduleNames.forEach((moduleName) => {
            serialNumbers[moduleName] = '';
        });
        return serialNumbers;
    }
    refresh() {
        this.worker = (() => {
            const setIntervalForSystemDisplayFacade = `
            setInterval(() => {postMessage("refresh");
                }, ${EmcConsts.REFRESH_INTERVAL}
            )`;
            const blob = new Blob([setIntervalForSystemDisplayFacade], { type: "application/javascript" });
            return new Worker(URL.createObjectURL(blob));
        })();
        this.worker.onmessage = (event) => {
            if (event.data == "refresh") {
                this.emcDisplayDataManager.updateSignals(this.emcSignals);
                this.updateActiveSystems();
                this.fetchSystemData();
                this.calculateSystemTestRunTime();
            }
        };
    }
    setIdleMonitorSignal(mode) {
        this.emcSignals.IdleMonitor = mode;
        this.emcSignals.EmcMode = mode;
    }
    setEmcAutomationSignal(mode) {
        let testDisable = !mode;
        if ((testDisable != this.emcSignals.TestDisable))
            this.emcSignals.TestDisable = testDisable;
    }
    setFullSystemSignal(mode) {
        this.emcSignals.FullSystem = mode;
    }
    classifyLine(line, isStatusPath) {
        let result;
        if (isStatusPath) {
            result = 'status';
        }
        else {
            const upper = (line !== null && line !== void 0 ? line : '').toUpperCase();
            if (upper.includes('FAULT') && !upper.includes('NO DETECTED FAULTS')) {
                result = 'fault';
            }
            else {
                result = 'normal';
            }
        }
        return result;
    }
    logChange(systemId, fullPath, value) {
        var _a, _b;
        const currentDateTime = new Date();
        let logMessage = "";
        if (this.faultWarningMessages.systemTesterTextMessage.get(fullPath) !== undefined) {
            const isStatusPath = this.faultWarningMessages.systemTesterStatusPaths.has(fullPath);
            if (((_a = this.faultWarningMessages.systemTesterFaultWarnings.get(fullPath)) === null || _a === void 0 ? void 0 : _a.get(Number(value))) !== undefined) {
                logMessage = currentDateTime.toLocaleString("en-US") + " " + systemId + " " +
                    this.faultWarningMessages.systemTesterTextMessage.get(fullPath) +
                    ((_b = this.faultWarningMessages.systemTesterFaultWarnings.get(fullPath)) === null || _b === void 0 ? void 0 : _b.get(Number(value))) + "\n";
            }
            else {
                logMessage = currentDateTime.toLocaleString("en-US") + " " + systemId + " " +
                    this.faultWarningMessages.systemTesterTextMessage.get(fullPath) +
                    SystemTesterConsts.unspecifiedFaults + "\n";
            }
            this.prependLogEntry(logMessage, this.classifyLine(logMessage, isStatusPath));
        }
    }
    getSerialNumberHeader() {
        const headerLines = [];
        const fullTesterName = this.getFullTesterName();
        if (fullTesterName) {
            headerLines.push(`User Name: ${fullTesterName}`);
        }
        const primarySerialNumber = this.serialNumber;
        if (primarySerialNumber) {
            headerLines.push(`Serial Number: ${primarySerialNumber}`);
        }
        const subsystemSerialNumberLines = [];
        this.moduleNames.forEach((moduleName) => {
            const serialNumber = this.subsystemSerialNumbers[moduleName] || 'Not Available';
            subsystemSerialNumberLines.push(`${moduleName} Serial Number: ${serialNumber}`);
        });
        if (subsystemSerialNumberLines.length > 0) {
            headerLines.push('Subsystem Serial Numbers -');
            headerLines.push(...subsystemSerialNumberLines);
        }
        if (headerLines.length === 0) {
            return '';
        }
        return `${headerLines.join('\n')}\n${'='.repeat(50)}\n`;
    }
    prependLogEntry(entry, kind) {
        if (entry) {
            const currentLog = this.messageText.toString();
            if (currentLog.length === 0 &&
                (this.isTesterNameLocked ||
                    this.isSerialNumberLocked ||
                    this.isSubsystemSerialNumbersLocked)) {
                const header = this.getSerialNumberHeader();
                this.messageText = header + entry;
                const entryLines = entry.split('\n').filter(l => l.trim().length > 0);
                entryLines.reverse().forEach(line => {
                    const lineKind = kind !== null && kind !== void 0 ? kind : this.classifyLine(line, false);
                    this.logEntries.unshift({ text: line, kind: lineKind });
                });
                const headerLines = header.split('\n').filter(l => l.trim().length > 0);
                headerLines.forEach(line => {
                    this.logEntries.push({ text: line, kind: 'normal' });
                });
                return;
            }
            this.messageText = entry + currentLog;
            const newLines = entry.split('\n').filter(l => l.trim().length > 0);
            newLines.reverse().forEach(line => {
                const lineKind = kind !== null && kind !== void 0 ? kind : this.classifyLine(line, false);
                this.logEntries.unshift({ text: line, kind: lineKind });
            });
        }
    }
    diffObject(systemId, prevData, currData, path = '') {
        if (currData == null)
            return;
        if (prevData === currData)
            return;
        Object.keys(currData).forEach(key => {
            const currValue = currData[key];
            const fullPath = path ? `${path}.${key}` : key;
            if (key !== "pdoData" && key !== "emcGUIOUT" && key !== "emcPIMPDO" && currValue !== null) {
                if (typeof currValue === 'object') {
                    this.diffObject(systemId, prevData === null || prevData === void 0 ? void 0 : prevData[key], currValue, fullPath);
                }
                else {
                    if ((prevData === null || prevData === void 0 ? void 0 : prevData[key]) !== currValue) {
                        this.logChange(systemId, fullPath, currValue);
                    }
                }
            }
        });
    }
    calculateSystemTestRunTime() {
        if (true == this.areSystemTestsRunning) {
            let runTime = (Date.now() - this.startSystemTestMonitorTimeStamp) / 1000;
            this.systemTestRuntime = runTime.toString();
        }
        else
            this.systemTestRuntime = '0';
    }
    setSubsystemSerialNumbers(subsystemSerialNumbers) {
        if (this.isSubsystemSerialNumbersLocked) {
            return;
        }
        const normalizedSerialNumbers = this.createEmptySubsystemSerialNumbers();
        this.moduleNames.forEach((moduleName) => {
            const subsystemSerialNumber = subsystemSerialNumbers[moduleName] || '';
            normalizedSerialNumbers[moduleName] = subsystemSerialNumber;
        });
        const allSubsystemSerialNumbersProvided = this.moduleNames.every((moduleName) => normalizedSerialNumbers[moduleName]);
        if (allSubsystemSerialNumbersProvided) {
            this.subsystemSerialNumbers = normalizedSerialNumbers;
            this.isSubsystemSerialNumbersLocked = true;
        }
    }
    setSerialNumber(serialNumber) {
        if (this.isSerialNumberLocked) {
            return;
        }
        const normalizedSerialNumber = (serialNumber || '');
        if (normalizedSerialNumber) {
            this.serialNumber = normalizedSerialNumber;
            this.isSerialNumberLocked = true;
        }
    }
    getSerialNumber() {
        return this.serialNumber;
    }
    getSubsystemSerialNumbers() {
        return Object.assign({}, this.subsystemSerialNumbers);
    }
    setTesterName(firstName, lastName) {
        if (this.isTesterNameLocked) {
            return;
        }
        const normalizedFirstName = firstName.trim();
        const normalizedLastName = lastName.trim();
        if (normalizedFirstName && normalizedLastName) {
            this.testerFirstName = normalizedFirstName;
            this.testerLastName = normalizedLastName;
            this.isTesterNameLocked = true;
        }
    }
    getTesterFirstName() {
        return this.testerFirstName;
    }
    getTesterLastName() {
        return this.testerLastName;
    }
    getFullTesterName() {
        if (!this.testerFirstName && !this.testerLastName) {
            return '';
        }
        return `${this.testerFirstName} ${this.testerLastName}`.trim();
    }
    getModuleNames() {
        return [...this.moduleNames];
    }
    resetAllSerialNumbers() {
        this.serialNumber = '';
        this.subsystemSerialNumbers = this.createEmptySubsystemSerialNumbers();
        this.isSerialNumberLocked = false;
        this.isSubsystemSerialNumbersLocked = false;
        this.testerFirstName = '';
        this.testerLastName = '';
        this.isTesterNameLocked = false;
    }
    resetTesterName() {
        this.testerFirstName = '';
        this.testerLastName = '';
        this.isTesterNameLocked = false;
    }
    prependSerialNumberHeader() {
        if (this.isTesterNameLocked || this.isSerialNumberLocked || this.isSubsystemSerialNumbersLocked) {
            const serialNumberHeader = this.getSerialNumberHeader();
            if (serialNumberHeader && !this.messageText.toString().startsWith(serialNumberHeader)) {
                this.messageText = serialNumberHeader + this.messageText.toString();
                const headerLines = serialNumberHeader.split('\n').filter(l => l.trim().length > 0);
                headerLines.forEach(line => {
                    this.logEntries.push({ text: line, kind: 'normal' });
                });
            }
        }
    }
    addMessageToMessageDisplay(message) {
        const now = new Date().toLocaleString("en-US");
        message = now + " " + message;
        this.prependLogEntry(message + '\n', 'normal');
    }
    checkForPacketDifferences(systemId, currentPacket, previousPacket) {
        console.log("Inside checkForPacketDifferences value of current packet is ", currentPacket, "value of previous packet is ", previousPacket);
        this.diffObject(systemId, previousPacket.emcDataParameterAllTabs, currentPacket.emcDataParameterAllTabs);
    }
    fetchSystemData() {
        this.activeSystemsList.forEach(system => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4;
            const systemId = system.systemId;
            let currentPacket = this.emcDisplayDataManager.fetchActiveSystemData(system.systemId);
            const previousPacket = this.previousPackets.get(systemId);
            if (currentPacket) {
                if (previousPacket) {
                    this.checkForPacketDifferences(systemId, currentPacket, previousPacket);
                }
                else {
                    if (this.hasTestEnded === false) {
                        let currentDateTime = new Date();
                        const versionData = currentPacket.emcSubsystemsVersionData;
                        const dataParamAllTabs = currentPacket.emcDataParameterAllTabs;
                        let versionMessages = '';
                        if (versionData) {
                            const timestamp = currentDateTime.toLocaleString("en-US");
                            const versions = [
                                { value: versionData.pim_version, label: SystemTesterConsts.pimVers },
                                { value: versionData.cssc_version, label: SystemTesterConsts.csscVers },
                                { value: versionData.csui_version, label: SystemTesterConsts.csuiVers },
                                { value: versionData.csic_version, label: SystemTesterConsts.csicVers },
                                { value: versionData.csrc_version, label: SystemTesterConsts.csrcVers },
                                { value: versionData.system_tester_ui_tool_version, label: SystemTesterConsts.systemTesterUIToolVers },
                                { value: versionData.emc_software_version, label: SystemTesterConsts.emcSoftwareVers },
                                { value: versionData.emc_ui_tool_version, label: SystemTesterConsts.emcUiToolVers },
                                { value: versionData.pssc_version, label: SystemTesterConsts.psscVers },
                                { value: versionData.psui_version, label: SystemTesterConsts.psuiVers },
                                { value: versionData.psic_version, label: SystemTesterConsts.psicVers },
                                { value: versionData.psrc_version, label: SystemTesterConsts.psrcVers },
                                { value: versionData.frame_version, label: SystemTesterConsts.frameboardVers },
                                { value: versionData.p34p5_version, label: SystemTesterConsts.armP34P5Vers },
                                { value: versionData.p1p2_version, label: SystemTesterConsts.armP1P2Vers },
                                { value: versionData.drive4_version, label: SystemTesterConsts.driveModuleVers4 },
                                { value: versionData.drive3_version, label: SystemTesterConsts.driveModuleVers3 },
                                { value: versionData.drive2_version, label: SystemTesterConsts.driveModuleVers2 },
                                { value: versionData.drive1_version, label: SystemTesterConsts.driveModuleVers1 }
                            ];
                            versions.forEach(({ value, label }) => {
                                if (value) {
                                    versionMessages += `${timestamp} ${label} ${value}\n`;
                                }
                            });
                        }
                        const drive1 = (_a = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _a === void 0 ? void 0 : _a.p_Drive_SensorStatusDrive1;
                        const drive2 = (_b = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _b === void 0 ? void 0 : _b.p_Drive_SensorStatusDrive2;
                        const drive3 = (_c = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _c === void 0 ? void 0 : _c.p_Drive_SensorStatusDrive3;
                        const drive4 = (_d = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _d === void 0 ? void 0 : _d.p_Drive_SensorStatusDrive4;
                        const armSensorStatus = (_e = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Arm) === null || _e === void 0 ? void 0 : _e.p_Arm_SensorStatus;
                        const ts = currentDateTime.toLocaleString("en-US");
                        let sensorStatusMessages = '';
                        if ((drive1 === null || drive1 === void 0 ? void 0 : drive1.CasetteOpenClose) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive1.CasetteOpenClose) === 0 ? SystemTesterConsts.module1 + SystemTesterConsts.cassetteOpen : SystemTesterConsts.module1 + SystemTesterConsts.cassetteClosed}\n`;
                        }
                        if ((drive1 === null || drive1 === void 0 ? void 0 : drive1.CasetteLockedUnlocked) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive1.CasetteLockedUnlocked) === 0 ? SystemTesterConsts.module1 + SystemTesterConsts.cassetteUnlocked : SystemTesterConsts.module1 + SystemTesterConsts.cassetteLocked}\n`;
                        }
                        if ((drive1 === null || drive1 === void 0 ? void 0 : drive1.CasetteTube) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive1.CasetteTube) === 0 ? SystemTesterConsts.module1 + SystemTesterConsts.cassetteTubeAttached : SystemTesterConsts.module1 + SystemTesterConsts.cassetteTubeDetached}\n`;
                        }
                        if ((drive2 === null || drive2 === void 0 ? void 0 : drive2.CasetteOpenClose) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive2.CasetteOpenClose) === 0 ? SystemTesterConsts.module2 + SystemTesterConsts.cassetteOpen : SystemTesterConsts.module2 + SystemTesterConsts.cassetteClosed}\n`;
                        }
                        if ((drive2 === null || drive2 === void 0 ? void 0 : drive2.CasetteLockedUnlocked) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive2.CasetteLockedUnlocked) === 0 ? SystemTesterConsts.module2 + SystemTesterConsts.cassetteUnlocked : SystemTesterConsts.module2 + SystemTesterConsts.cassetteLocked}\n`;
                        }
                        if ((drive2 === null || drive2 === void 0 ? void 0 : drive2.CasetteTube) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive2.CasetteTube) === 0 ? SystemTesterConsts.module2 + SystemTesterConsts.cassetteTubeAttached : SystemTesterConsts.module2 + SystemTesterConsts.cassetteTubeDetached}\n`;
                        }
                        if ((drive3 === null || drive3 === void 0 ? void 0 : drive3.CasetteOpenClose) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive3.CasetteOpenClose) === 0 ? SystemTesterConsts.module3 + SystemTesterConsts.cassetteOpen : SystemTesterConsts.module3 + SystemTesterConsts.cassetteClosed}\n`;
                        }
                        if ((drive3 === null || drive3 === void 0 ? void 0 : drive3.CasetteLockedUnlocked) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive3.CasetteLockedUnlocked) === 0 ? SystemTesterConsts.module3 + SystemTesterConsts.cassetteUnlocked : SystemTesterConsts.module3 + SystemTesterConsts.cassetteLocked}\n`;
                        }
                        if ((drive3 === null || drive3 === void 0 ? void 0 : drive3.CasetteTube) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive3.CasetteTube) === 0 ? SystemTesterConsts.module3 + SystemTesterConsts.cassetteTubeAttached : SystemTesterConsts.module3 + SystemTesterConsts.cassetteTubeDetached}\n`;
                        }
                        if ((drive4 === null || drive4 === void 0 ? void 0 : drive4.CasetteOpenClose) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive4.CasetteOpenClose) === 0 ? SystemTesterConsts.module4 + SystemTesterConsts.cassetteOpen : SystemTesterConsts.module4 + SystemTesterConsts.cassetteClosed}\n`;
                        }
                        if ((drive4 === null || drive4 === void 0 ? void 0 : drive4.CasetteLockedUnlocked) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive4.CasetteLockedUnlocked) === 0 ? SystemTesterConsts.module4 + SystemTesterConsts.cassetteUnlocked : SystemTesterConsts.module4 + SystemTesterConsts.cassetteLocked}\n`;
                        }
                        if ((drive4 === null || drive4 === void 0 ? void 0 : drive4.CasetteTube) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive4.CasetteTube) === 0 ? SystemTesterConsts.module4 + SystemTesterConsts.cassetteTubeAttached : SystemTesterConsts.module4 + SystemTesterConsts.cassetteTubeDetached}\n`;
                        }
                        if ((armSensorStatus === null || armSensorStatus === void 0 ? void 0 : armSensorStatus.ProximalAnchor) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(armSensorStatus.ProximalAnchor) === 0 ? SystemTesterConsts.frameProximalDetectDetached : SystemTesterConsts.frameProximalDetectAttached}\n`;
                        }
                        if ((armSensorStatus === null || armSensorStatus === void 0 ? void 0 : armSensorStatus.DistalAnchor) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(armSensorStatus.DistalAnchor) === 0 ? SystemTesterConsts.frameDistalDetectDetached : SystemTesterConsts.frameDistalDetectAttached}\n`;
                        }
                        if ((armSensorStatus === null || armSensorStatus === void 0 ? void 0 : armSensorStatus.ArmPresentRemoved) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(armSensorStatus.ArmPresentRemoved) === 0 ? SystemTesterConsts.armUnlockButtonReleased : SystemTesterConsts.armUnlockButtonPressed}\n`;
                        }
                        if ((drive1 === null || drive1 === void 0 ? void 0 : drive1.Estop) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive1.Estop) === 0 ? SystemTesterConsts.module1 + SystemTesterConsts.estopInactive : SystemTesterConsts.module1 + SystemTesterConsts.estopActive}\n`;
                        }
                        if ((drive2 === null || drive2 === void 0 ? void 0 : drive2.Estop) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive2.Estop) === 0 ? SystemTesterConsts.module2 + SystemTesterConsts.estopInactive : SystemTesterConsts.module2 + SystemTesterConsts.estopActive}\n`;
                        }
                        if ((drive3 === null || drive3 === void 0 ? void 0 : drive3.Estop) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive3.Estop) === 0 ? SystemTesterConsts.module3 + SystemTesterConsts.estopInactive : SystemTesterConsts.module3 + SystemTesterConsts.estopActive}\n`;
                        }
                        if ((drive4 === null || drive4 === void 0 ? void 0 : drive4.Estop) !== undefined) {
                            sensorStatusMessages += `${ts} ${systemId} ${Number(drive4.Estop) === 0 ? SystemTesterConsts.module4 + SystemTesterConsts.estopInactive : SystemTesterConsts.module4 + SystemTesterConsts.estopActive}\n`;
                        }
                        let faultCodeMessages = '';
                        if (((_g = (_f = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Arm) === null || _f === void 0 ? void 0 : _f.p_Arm_P1P2) === null || _g === void 0 ? void 0 : _g.M1ErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterArmFaultWarningMap.get(Number(dataParamAllTabs.emcDataParameter_Arm.p_Arm_P1P2.M1ErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.p1Fault}${this.faultWarningMessages.systemTesterArmFaultWarningMap.get(Number(dataParamAllTabs.emcDataParameter_Arm.p_Arm_P1P2.M1ErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.p1Fault} Joint has an unspecified fault \n`;
                        }
                        if (((_j = (_h = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Arm) === null || _h === void 0 ? void 0 : _h.p_Arm_P1P2) === null || _j === void 0 ? void 0 : _j.M2ErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterArmFaultWarningMap.get(Number(dataParamAllTabs.emcDataParameter_Arm.p_Arm_P1P2.M2ErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.p2Fault}${this.faultWarningMessages.systemTesterArmFaultWarningMap.get(Number(dataParamAllTabs.emcDataParameter_Arm.p_Arm_P1P2.M2ErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.p2Fault} Joint has an unspecified fault \n`;
                        }
                        if (((_l = (_k = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Arm) === null || _k === void 0 ? void 0 : _k.p_Arm_P34P5) === null || _l === void 0 ? void 0 : _l.M2ErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterArmFaultWarningMap.get(Number(dataParamAllTabs.emcDataParameter_Arm.p_Arm_P34P5.M2ErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.p34Fault}${this.faultWarningMessages.systemTesterArmFaultWarningMap.get(Number(dataParamAllTabs.emcDataParameter_Arm.p_Arm_P34P5.M2ErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.p34Fault} Joint has an unspecified fault \n`;
                        }
                        if (((_o = (_m = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Arm) === null || _m === void 0 ? void 0 : _m.p_Arm_P34P5) === null || _o === void 0 ? void 0 : _o.M1ErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterArmFaultWarningMap.get(Number(dataParamAllTabs.emcDataParameter_Arm.p_Arm_P34P5.M1ErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.p5Fault}${this.faultWarningMessages.systemTesterArmFaultWarningMap.get(Number(dataParamAllTabs.emcDataParameter_Arm.p_Arm_P34P5.M1ErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.p5Fault} Joint has an unspecified fault \n`;
                        }
                        if (((_q = (_p = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _p === void 0 ? void 0 : _p.p_Drive_Drive1) === null || _q === void 0 ? void 0 : _q.LinErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterLinearMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive1.LinErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module1}${this.faultWarningMessages.systemTesterLinearMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive1.LinErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module1} Linear Motor has an unspecified fault \n`;
                        }
                        if (((_s = (_r = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _r === void 0 ? void 0 : _r.p_Drive_Drive1) === null || _s === void 0 ? void 0 : _s.RotErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterRotationalMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive1.RotErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module1}${this.faultWarningMessages.systemTesterRotationalMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive1.RotErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module1} Rotational Motor has an unspecified fault \n`;
                        }
                        if (((_u = (_t = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _t === void 0 ? void 0 : _t.p_Drive_Drive2) === null || _u === void 0 ? void 0 : _u.LinErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterLinearMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive2.LinErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module2}${this.faultWarningMessages.systemTesterLinearMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive2.LinErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module2} Linear Motor has an unspecfied fault \n`;
                        }
                        if (((_w = (_v = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _v === void 0 ? void 0 : _v.p_Drive_Drive2) === null || _w === void 0 ? void 0 : _w.RotErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterRotationalMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive2.RotErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module2}${this.faultWarningMessages.systemTesterRotationalMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive2.RotErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module2} Rotational Motor has an unspecified fault \n`;
                        }
                        if (((_y = (_x = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _x === void 0 ? void 0 : _x.p_Drive_Drive3) === null || _y === void 0 ? void 0 : _y.LinErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterLinearMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive3.LinErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module3}${this.faultWarningMessages.systemTesterLinearMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive3.LinErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module3} Linear Motor has an unspecified fault \n`;
                        }
                        if (((_0 = (_z = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _z === void 0 ? void 0 : _z.p_Drive_Drive3) === null || _0 === void 0 ? void 0 : _0.RotErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterRotationalMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive3.RotErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module3}${this.faultWarningMessages.systemTesterRotationalMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive3.RotErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module3} Rotational Motor has an unspecified fault \n`;
                        }
                        if (((_2 = (_1 = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _1 === void 0 ? void 0 : _1.p_Drive_Drive4) === null || _2 === void 0 ? void 0 : _2.LinErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterLinearMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive4.LinErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module4}${this.faultWarningMessages.systemTesterLinearMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive4.LinErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module4} Linear Motor has an unspecified fault \n`;
                        }
                        if (((_4 = (_3 = dataParamAllTabs === null || dataParamAllTabs === void 0 ? void 0 : dataParamAllTabs.emcDataParameter_Drive) === null || _3 === void 0 ? void 0 : _3.p_Drive_Drive4) === null || _4 === void 0 ? void 0 : _4.RotErrorCode) !== undefined) {
                            const val = this.faultWarningMessages.systemTesterRotationalMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive4.RotErrorCode.toString()));
                            if (val !== undefined)
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module4}${this.faultWarningMessages.systemTesterRotationalMotorFaults.get(Number(dataParamAllTabs.emcDataParameter_Drive.p_Drive_Drive4.RotErrorCode.toString()))}\n`;
                            else
                                faultCodeMessages += `${currentDateTime.toLocaleString("en-US")} ${systemId} ${SystemTesterConsts.module4} Rotational Motor has an unspecified fault \n`;
                        }
                        this.prependLogEntry(faultCodeMessages);
                        this.prependLogEntry(sensorStatusMessages, 'status');
                        this.prependLogEntry(versionMessages, 'normal');
                    }
                }
                this.previousPackets.set(systemId, currentPacket);
            }
        });
    }
    updateActiveSystems() {
        if (this.prevActiveSystemsList.length > this.activeSystemsList.length) {
            let idsOfActiveSystem = this.activeSystemsList.map(system => system.systemId);
            this.prevActiveSystemsList.forEach(removedSystem => {
                if (false == idsOfActiveSystem.includes(removedSystem.systemId)) {
                    this.emcDisplayDataManager.removeInactiveSystem(removedSystem.systemId);
                }
            });
        }
        this.activeSystemsList.forEach(system => {
            this.emcDisplayDataManager.updateActiveSystem(system.systemId);
        });
        this.prevActiveSystemsList = Array.from(this.activeSystemsList);
    }
}
//# sourceMappingURL=SystemTesterDisplayFacade.js.map